import { useMemo } from 'react';
import { manifest, content } from './manifest.generated';
import { useAdminDocsHost } from './host-context';
import type { DocCategory, DocManifestEntry } from './types';

export interface UseDocsOptions {
  category?: DocCategory;
  /** Case-insensitive substring matched against title + summary + category. */
  query?: string;
  /**
   * When true (default), filter out docs whose `requiredPermission` is not
   * in the current host's permission set.
   */
  respectPermissions?: boolean;
}

export interface UseDocsResult {
  docs: DocManifestEntry[];
  totalCount: number;
}

/**
 * List admin guides, filtered by category, free-text query, and the
 * current user's permissions.
 */
export function useDocs(options: UseDocsOptions = {}): UseDocsResult {
  const { category, query, respectPermissions = true } = options;
  const { permissions } = useAdminDocsHost();

  return useMemo(() => {
    let docs = manifest.slice();

    if (respectPermissions) {
      docs = docs.filter((d) => permissions.includes(d.requiredPermission));
    }
    if (category) {
      docs = docs.filter((d) => d.category === category);
    }
    if (query && query.trim()) {
      const needle = query.trim().toLowerCase();
      docs = docs.filter((d) => {
        return (
          d.title.toLowerCase().includes(needle) ||
          d.summary.toLowerCase().includes(needle) ||
          d.category.toLowerCase().includes(needle)
        );
      });
    }

    docs.sort((a, b) => {
      if (a.category !== b.category) return a.category.localeCompare(b.category);
      if (a.order !== b.order) return a.order - b.order;
      return a.title.localeCompare(b.title);
    });

    return { docs, totalCount: docs.length };
  }, [category, query, respectPermissions, permissions]);
}

/** All manifest entries, ignoring permissions and filters. Useful for tests. */
export function getAllDocs(): DocManifestEntry[] {
  return manifest.slice();
}

/** Internal accessor used by `useDoc`. Exported for tests. */
export function getDocContent(slug: string): string | null {
  return content[slug] ?? null;
}
